import React from 'react';
import { X } from 'lucide-react';

/**
 * Badge Component
 * 
 * Production-grade badge for status indicators, labels, and counts:
 * - Variants (default, success, error, warning, info, primary, secondary)
 * - Size variants (sm, md, lg)
 * - Dot indicator (status dot)
 * - Pulse animation (for live indicators)
 * - Icon support
 * - Removable (with X button)
 * - Dark mode compatible
 * - Touch-friendly remove button on mobile
 * 
 * Industry Pattern: GitHub labels, Linear status badges, Stripe payment status
 * 
 * @example
 * ```tsx
 * <Badge variant="success" dot pulse>Online</Badge>
 * <Badge variant="warning" icon={<AlertTriangle />}>Pending</Badge>
 * <Badge variant="error" removable onRemove={() => {}}>Error</Badge>
 * ```
 */

export interface BadgeProps {
  /** Badge variant (color scheme) */
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary' | 'running' | 'generating' | 'passed' | 'failed' | 'cancelled' | 'unstable';
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show dot indicator */
  dot?: boolean;
  /** Animated pulse effect (for live status) */
  pulse?: boolean;
  /** Icon element */
  icon?: React.ReactNode;
  /** Can be removed */
  removable?: boolean;
  /** Remove callback */
  onRemove?: () => void;
  /** Badge content */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'md',
  dot = false,
  pulse = false,
  icon,
  removable = false,
  onRemove,
  children,
  className = '',
}) => {
  // Size classes
  const sizeClasses = {
    sm: {
      badge: 'text-xs px-2 py-0.5',
      icon: 'w-3 h-3',
      dot: 'w-1.5 h-1.5',
      remove: 'w-3 h-3 ml-1',
    },
    md: {
      badge: 'text-sm px-2.5 py-1',
      icon: 'w-3.5 h-3.5',
      dot: 'w-2 h-2',
      remove: 'w-3.5 h-3.5 ml-1.5',
    },
    lg: {
      badge: 'text-base px-3 py-1.5',
      icon: 'w-4 h-4',
      dot: 'w-2.5 h-2.5',
      remove: 'w-4 h-4 ml-2',
    },
  };

  // Variant classes (background, text, border)
  const variantClasses = {
    default: {
      badge: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 border-gray-300 dark:border-slate-600',
      dot: 'bg-gray-500 dark:bg-slate-400',
      remove: 'hover:bg-gray-200 dark:hover:bg-slate-600',
    },
    primary: {
      badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
      dot: 'bg-blue-500 dark:bg-blue-400',
      remove: 'hover:bg-blue-200 dark:hover:bg-blue-800',
    },
    secondary: {
      badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700',
      dot: 'bg-purple-500 dark:bg-purple-400',
      remove: 'hover:bg-purple-200 dark:hover:bg-purple-800',
    },
    success: {
      badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
      dot: 'bg-green-500 dark:bg-green-400',
      remove: 'hover:bg-green-200 dark:hover:bg-green-800',
    },
    error: {
      badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700',
      dot: 'bg-red-500 dark:bg-red-400',
      remove: 'hover:bg-red-200 dark:hover:bg-red-800',
    },
    warning: {
      badge: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700',
      dot: 'bg-yellow-500 dark:bg-yellow-400',
      remove: 'hover:bg-yellow-200 dark:hover:bg-yellow-800',
    },
    info: {
      badge: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700',
      dot: 'bg-cyan-500 dark:bg-cyan-400',
      remove: 'hover:bg-cyan-200 dark:hover:bg-cyan-800',
    },
    running: {
      badge: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
      dot: 'bg-blue-500 dark:bg-blue-400',
      remove: 'hover:bg-blue-200 dark:hover:bg-blue-800',
    },
    generating: {
      badge: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
      dot: 'bg-indigo-500 dark:bg-indigo-400',
      remove: 'hover:bg-indigo-200 dark:hover:bg-indigo-800',
    },
    passed: {
      badge: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
      dot: 'bg-emerald-500 dark:bg-emerald-400',
      remove: 'hover:bg-emerald-200 dark:hover:bg-emerald-800',
    },
    failed: {
      badge: 'bg-rose-100 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
      dot: 'bg-rose-500 dark:bg-rose-400',
      remove: 'hover:bg-rose-200 dark:hover:bg-rose-800',
    },
    cancelled: {
      badge: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 border-gray-300 dark:border-slate-600',
      dot: 'bg-gray-500 dark:bg-slate-400',
      remove: 'hover:bg-gray-200 dark:hover:bg-slate-600',
    },
    unstable: {
      badge: 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800',
      dot: 'bg-amber-500 dark:bg-amber-400',
      remove: 'hover:bg-amber-200 dark:hover:bg-amber-800',
    },
  };

  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  return (
    <span
      className={`
        inline-flex
        items-center
        gap-1
        font-medium
        rounded-full
        border
        ${currentSize.badge}
        ${currentVariant.badge}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {/* Dot Indicator */}
      {dot && (
        <span
          className={`
            ${currentSize.dot}
            ${currentVariant.dot}
            rounded-full
            flex-shrink-0
            ${pulse ? 'animate-pulse' : ''}
          `.trim().replace(/\s+/g, ' ')}
          aria-hidden="true"
        />
      )}

      {/* Icon */}
      {icon && (
        <span className={`${currentSize.icon} flex-shrink-0`} aria-hidden="true">
          {icon}
        </span>
      )}

      {/* Content */}
      <span className="truncate">{children}</span>

      {/* Remove Button */}
      {removable && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={`
            ${currentSize.remove}
            flex-shrink-0
            rounded-full
            transition-colors
            focus:outline-none
            focus:ring-2
            focus:ring-offset-1
            focus:ring-blue-500
            dark:focus:ring-blue-400
            ${currentVariant.remove}
            min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0
            flex items-center justify-center
            -mr-1
          `.trim().replace(/\s+/g, ' ')}
          aria-label="Remove badge"
        >
          <X className={currentSize.remove} strokeWidth={2.5} />
        </button>
      )}
    </span>
  );
};

Badge.displayName = 'Badge';

